import { useState, useEffect } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type UseMutationResult,
} from '@tanstack/react-query';
import type { SearchRequest } from '@vagusx/shared';
import type { CreatePatientInEhrRequest, PatientResponse } from '@vagusx/clients';
import { getPatientClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';

/** Debounce a value by the given delay (ms). */
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/**
 * Hook to fetch and search patients with server-side pagination.
 */
export function usePatients(options?: { limit: number; offset: number; searchTerm?: string }) {
  const { limit = 50, offset = 0, searchTerm } = options ?? {};

  const request: SearchRequest = {
    queries: searchTerm ? [{ field: '_search', operator: 'like', value: searchTerm }] : [],
    limit,
    offset,
  };

  return useQuery({
    queryKey: queryKeys.patients.list(request),
    queryFn: () => getPatientClient().search(request),
  });
}

/**
 * Hook to fetch a single patient by ID.
 */
export function usePatient(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.patients.detail(id ?? ''),
    queryFn: () => getPatientClient().getById(id!),
    enabled: !!id,
  });
}

/**
 * Create a patient in the EHR from the New patient dialog. The dialog renders
 * every failure itself — a 409 is a duplicate it offers to resolve, not an error
 * to toast — so the global toast is suppressed.
 */
export function useCreatePatientInEhr(): UseMutationResult<
  PatientResponse,
  Error,
  CreatePatientInEhrRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePatientInEhrRequest) => getPatientClient().createInEhr(data),
    meta: { suppressGlobalError: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.all });
    },
  });
}

/**
 * Hook to flag a patient.
 */
export function useFlagPatient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => getPatientClient().flagPatient(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.detail(id) });
    },
  });
}

/**
 * Hook to unflag a patient.
 */
export function useUnflagPatient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => getPatientClient().unflagPatient(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.detail(id) });
    },
  });
}

/**
 * Hook to get patient flag status.
 */
export function usePatientFlagStatus(patientId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.patients.all, 'flag-status', patientId] as const,
    queryFn: () => getPatientClient().getFlagStatus(patientId!),
    enabled: !!patientId,
  });
}

/**
 * Hook for patient search with debouncing.
 * Searches by name, phone, DOB, MRN via server-side ILIKE.
 * Returns up to 10 results. Requires at least 2 characters.
 */
export function usePatientSearch(searchTerm: string) {
  const debouncedTerm = useDebounce(searchTerm, 300);

  const request: SearchRequest = {
    queries: debouncedTerm ? [{ field: '_search', operator: 'like', value: debouncedTerm }] : [],
    limit: 10,
    offset: 0,
  };

  return useQuery({
    queryKey: queryKeys.patients.list(request),
    queryFn: () => getPatientClient().search(request),
    enabled: debouncedTerm.length >= 2,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}
